import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Calendar, 
  Clock, 
  User, 
  Mail, 
  Phone, 
  Heart, 
  CheckCircle, 
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  Stethoscope,
  Check,
  Lock,
  Unlock,
  X
} from 'lucide-react';
import { hospitalInfo } from '../mock';
import { useBusinessInfo } from '../hooks/useBusinessInfo';
import { useAuth } from '../contexts/AuthContext';

const UrgentCareBooking = () => {
  const { businessInfo: currentBusinessInfo } = useBusinessInfo();
  const [currentTab, setCurrentTab] = useState(0);
  const [completedTabs, setCompletedTabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [timeSlots, setTimeSlots] = useState([]);
  const [availableToday, setAvailableToday] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [todayHours, setTodayHours] = useState(null);
  const { user } = useAuth(); // Get authentication context
  
  // Helper function to check if user is staff member
  const isStaffMember = user && user.role && (user.role === 'manager' || user.role === 'technician' || user.role === 'admin');

  const [formData, setFormData] = useState({
    appointment_time: '',
    owner_first_name: '',
    owner_last_name: '',
    email: '',
    phone: '',
    pet_name: '',
    pet_type: '',
    reason_for_visit: '',
    primary_vet_hospital: '',
    how_heard_about_us: ''
  });

  // New state for blocked slots functionality
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [selectedSlotForBlock, setSelectedSlotForBlock] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  
  // State for unblock confirmation modal
  const [showUnblockModal, setShowUnblockModal] = useState(false);
  const [selectedSlotForUnblock, setSelectedSlotForUnblock] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");
  const primaryColor = 'var(--theme-primary)';

  const tabs = [
    { id: 0, title: 'Select Time', icon: Clock },
    { id: 1, title: 'Client Details', icon: User },
    { id: 2, title: 'Pet Details', icon: Heart },
    { id: 3, title: 'Visit Reason', icon: Stethoscope },
    { id: 4, title: 'Other Details', icon: CheckCircle },
    { id: 5, title: 'Review', icon: AlertCircle },
    { id: 6, title: 'Confirmation', icon: Check }
  ];

  const reasonOptions = [
    'Sick / Illness',
    'Injury',
    'Joint Pain / Limping',
    'Skin / Ears / Itching / Allergy',
    'Coughing / Congestion / Nasal Discharge',
    'Difficulty Breathing',
    'Urinary Problems',
    'Vomiting / Diarrhea / Gastrointestinal',
    'Not Eating',
    'Eye Issues',
    'Poison / Ingestion',
    'End of Life Care'
  ];

  const howHeardOptions = [
    'Google Search',
    'Social Media',
    'Friend/Family Referral',
    'Veterinarian Referral',
    'Drive By/Location',
    'Online Reviews',
    'Pet Insurance',
    'Emergency Situation',
    'Other'
  ];

  useEffect(() => {
    fetchTimeSlots();
  }, []);

  const fetchTimeSlots = async () => {
    setLoading(true);
    try {
      // Get business info to determine the correct business timezone date
      const businessResponse = await fetch(`${API_BASE_URL}/api/business-info`);
      const businessData = await businessResponse.json();
      
      // Use business timezone (default to America/New_York if not configured)
      const businessTimezone = businessData.timezone || 'America/New_York';
      
      // Get today's date in business timezone
      const today = new Date().toLocaleDateString('en-CA', { 
        timeZone: businessTimezone 
      }); // en-CA gives YYYY-MM-DD format
      
      setSelectedDate(today); // Set the selected date
      
      const response = await fetch(`${API_BASE_URL}/api/urgent-care-time-slots/${today}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      
      if (data.available) {
        setTimeSlots(data.slots);
        setAvailableToday(true);
        setTodayHours(data.hours);
        setMessage({ type: '', text: '' });
      } else {
        setAvailableToday(false);
        setTimeSlots([]);
        setTodayHours(null);
        setMessage({ type: 'error', text: data.message || 'Urgent Care is not available today' });
      }

      // No need to fetch blocked slots separately anymore - they're included in the main API response
      
    } catch (error) {
      console.error('Error fetching time slots:', error);
      setAvailableToday(false);
      setMessage({ type: 'error', text: 'Unable to load available times. Please try again or call us directly.' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setMessage({ type: '', text: '' });
  };

  const validateCurrentTab = () => {
    switch (currentTab) {
      case 0:
        return formData.appointment_time !== '';
      case 1:
        if (isStaffMember) {
          // For staff: only require names and phone number
          return formData.owner_first_name && formData.owner_last_name && formData.phone &&
                 /^\d{10,}$/.test(formData.phone.replace(/\D/g, ''));
        } else {
          // For regular users: require all fields including email
          return formData.owner_first_name && formData.owner_last_name && 
                 formData.email && formData.phone &&
                 /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) &&
                 /^\d{10,}$/.test(formData.phone.replace(/\D/g, ''));
        }
      case 2:
        if (isStaffMember) {
          // For staff: pet details are optional
          return true;
        } else {
          // For regular users: require pet details
          return formData.pet_name && formData.pet_type;
        }
      case 3:
        if (isStaffMember) {
          // For staff: visit reason is optional
          return true;
        } else {
          // For regular users: require visit reason
          return formData.reason_for_visit;
        }
      case 4:
        return true; // Primary vet hospital and how heard about us are always optional
      default:
        return false;
    }
  };

  const goToNextTab = () => {
    if (validateCurrentTab()) {
      if (!completedTabs.includes(currentTab)) {
        setCompletedTabs([...completedTabs, currentTab]);
      }
      setCurrentTab(currentTab + 1);
      setMessage({ type: '', text: '' });
      // Scroll to top when advancing to next tab
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setMessage({ type: 'error', text: 'Please complete all required fields' });
    }
  };

  const goToTab = (tabIndex) => {
    // Can only go to completed tabs or the next available tab
    if (completedTabs.includes(tabIndex) || tabIndex === Math.min(...completedTabs) + 1 || tabIndex === 0) {
      setCurrentTab(tabIndex);
      setMessage({ type: '', text: '' });
      // Scroll to top when changing tabs
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const isTabEnabled = (tabIndex) => {
    return tabIndex === 0 || completedTabs.includes(tabIndex - 1);
  };

  const isTabCompleted = (tabIndex) => {
    return completedTabs.includes(tabIndex);
  };

  const prevStep = () => {
    if (currentTab > 0) {
      setCurrentTab(currentTab - 1);
      setMessage({ type: '', text: '' });
      // Scroll to top when going back
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const nextStep = () => {
    goToNextTab();
  };

  const validateStep = () => {
    return validateCurrentTab();
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const hour12 = parseInt(hours) % 12 || 12;
    const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
    return `${hour12}:${minutes} ${ampm}`;
  };

  const formatAppointmentTime = (appointmentTimeString) => {
    if (!appointmentTimeString) return '';
    
    try {
      // Since appointments are now stored in business timezone, parse directly
      const date = new Date(appointmentTimeString);
      
      // Format for display (already in business timezone)
      const businessTime = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      
      console.log(`UrgentCareBooking formatAppointmentTime: ${appointmentTimeString} -> ${businessTime} (Business timezone)`);
      return businessTime;
    } catch (error) {
      console.error('Error formatting appointment time:', error);
      // Fallback to original time if parsing fails
      return formatTime(appointmentTimeString.split('T')[1]);
    }
  };

  const formatDateAndDay = () => {
    const today = new Date();
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return today.toLocaleDateString('en-US', options);
  };

  const formatPhoneNumber = (phone) => {
    // Remove all non-digits
    const cleaned = phone.replace(/\D/g, '');
    // Format as XXX-XXX-XXXX
    if (cleaned.length === 10) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
    }
    return phone; // Return original if not 10 digits
  };

  const capitalizeWords = (str) => {
    return str.replace(/\b\w/g, l => l.toUpperCase());
  };

  const submitAppointment = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/urgent-care-appointments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setCurrentTab(6); // Success tab
        setCompletedTabs([...completedTabs, 5]);
        setMessage({ type: 'success', text: 'Appointment booked successfully!' });
        // Scroll to top when confirmation page loads
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || 'Failed to book appointment' });
      }
    } catch (error) {
      console.error('Error submitting appointment:', error);
      setMessage({ type: 'error', text: 'Unable to book appointment. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  // Blocked Slots Functions
  const openBlockModal = (slotTime, slotDisplay) => {
    setSelectedSlotForBlock({ time: slotTime, display: slotDisplay });
    setBlockReason('');
    setShowBlockModal(true);
  };

  const closeBlockModal = () => {
    setShowBlockModal(false);
    setSelectedSlotForBlock(null);
    setBlockReason('');
  };

  const handleBlockSlot = async () => {
    if (!selectedSlotForBlock) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/blocked-slots`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          date: selectedDate,
          time: selectedSlotForBlock.time,
          reason: blockReason || 'Staff blocked slot'
        })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Time slot blocked successfully!' });
        closeBlockModal();
        // Refresh time slots (blocked slots info included)
        fetchTimeSlots();
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || 'Failed to block slot' });
      }
    } catch (error) {
      console.error('Error blocking slot:', error);
      setMessage({ type: 'error', text: 'Unable to block slot. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleUnblockSlot = async (slotId, slotTime) => {
    // Store slot info and show confirmation modal instead of browser confirm
    setSelectedSlotForUnblock({ id: slotId, time: slotTime });
    setShowUnblockModal(true);
  };

  // New function to handle the actual unblock after confirmation
  const confirmUnblockSlot = async () => {
    if (!selectedSlotForUnblock) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/blocked-slots/${selectedSlotForUnblock.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Time slot unblocked successfully!' });
        // Refresh time slots (blocked slots info included)
        fetchTimeSlots();
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || 'Failed to unblock slot' });
      }
    } catch (error) {
      console.error('Error unblocking slot:', error);
      setMessage({ type: 'error', text: 'Failed to unblock slot' });
    } finally {
      setLoading(false);
      closeUnblockModal();
    }
  };

  // Close unblock modal
  const closeUnblockModal = () => {
    setShowUnblockModal(false);
    setSelectedSlotForUnblock(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header - Inside the main card */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Urgent Care - Online Check In
              </h2>
            </div>
          </div>

          {/* Content Area */}
          <div className="p-6">
            {/* Tab Navigation - Styled like Business Info */}
            <div className="bg-white rounded-lg shadow-sm mb-4 sm:mb-6 border border-gray-200">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
                  {tabs.map((tab) => {
                    const IconComponent = tab.icon;
                    const isActive = currentTab === tab.id;
                    const isCompleted = isTabCompleted(tab.id);
                    const isEnabled = isTabEnabled(tab.id);
                    
                    return (
                      <button
                        key={tab.id}
                        onClick={() => goToTab(tab.id)}
                        disabled={!isEnabled}
                        className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                          isActive
                            ? 'border-theme-blue text-theme-blue'
                            : isCompleted
                            ? 'border-theme-blue text-theme-blue'
                            : isEnabled
                            ? 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            : 'border-transparent text-gray-300 cursor-not-allowed'
                        }`}
                      >
                        {isCompleted ? (
                          <Check className="h-4 w-4" />
                        ) : (
                          <IconComponent className="h-4 w-4" />
                        )}
                        <span>{tab.title}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-6">
            {/* Messages */}
            {message.text && (
              <div className={`mb-6 p-4 rounded-lg flex items-center ${
                message.type === 'success' ? 'border' : 'bg-red-50 border border-red-200'
              }`}
              style={message.type === 'success' ? {
                backgroundColor: 'var(--theme-primary-light)',
                borderColor: 'var(--theme-primary)'
              } : {}}
              >
                <AlertCircle className={`h-5 w-5 mr-3 ${
                  message.type === 'success' ? '' : 'text-red-600'
                }`} 
                style={message.type === 'success' ? { color: primaryColor } : {}}
                />
                <p className={`${
                  message.type === 'success' ? '' : 'text-red-800'
                }`}
                style={message.type === 'success' ? { color: '#1e7a8c' } : {}}
                >
                  {message.text}
                </p>
              </div>
            )}

            {/* Tab 0: Time Selection */}
            {currentTab === 0 && (
              <div>
                <div className="mb-4">
                  <h2 className="text-base font-semibold text-gray-900 mb-1">Select an Arrival Time for Today</h2>
                  <p className="text-sm text-gray-600">
                    {formatDateAndDay()}
                    {todayHours && (
                      <span className="block mt-0.5 text-xs" style={{ color: primaryColor }}>
                        Urgent Care Hours: {todayHours.open} - {todayHours.close}
                      </span>
                    )}
                  </p>
                </div>

                {loading ? (
                  <div className="text-center py-8">
                    <div 
                      className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto"
                      style={{ borderColor: primaryColor }}
                    ></div>
                    <p className="mt-2 text-gray-600">Loading available times...</p>
                  </div>
                ) : !availableToday ? (
                  <div className="text-center py-8 space-y-4">
                    <Clock className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <div>
                      <p className="text-gray-600 mb-4">{message.text}</p>
                      <div className="bg-red-50 border-2 border-red-200 p-6 rounded-xl max-w-lg mx-auto">
                        <div className="flex items-start">
                          <AlertCircle className="h-6 w-6 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                          <div className="text-left">
                            <h3 className="text-lg font-bold text-red-900 mb-2">Life-Threatening Emergencies</h3>
                            <p className="text-red-700 mb-2">
                              For emergencies outside our hours, contact the nearest 24-hour emergency hospital:
                            </p>
                            {currentBusinessInfo?.referral_hospital_name && currentBusinessInfo?.referral_hospital_phone ? (
                              <a 
                                href={`tel:${currentBusinessInfo.referral_hospital_phone}`}
                                className="text-red-600 font-semibold hover:text-red-800 transition-colors"
                              >
                                {currentBusinessInfo.referral_hospital_name}: {currentBusinessInfo.referral_hospital_phone}
                              </a>
                            ) : (
                              <p className="text-red-600 font-semibold">
                                VCA SouthPaws: (703) 752-9100
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : timeSlots.length === 0 ? (
                  <div className="text-center py-8 space-y-4">
                    <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <div>
                      <p className="text-lg text-gray-900 mb-2">All Slots Booked</p>
                      <p className="text-gray-600 mb-4">All appointment slots for today are booked.</p>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto">
                        <div className="flex items-center">
                          <Phone className="h-5 w-5 mr-3" style={{ color: primaryColor }} />
                          <div>
                            <p className="text-gray-900 font-medium">Call for Cancellations</p>
                            <p className="text-gray-700 text-sm">{currentBusinessInfo.phone}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {timeSlots.map((slot) => {
                        const isBlocked = slot.is_blocked || false;
                        const blockedInfo = slot.blocked_info;
                        
                        return (
                          <div key={slot.value} className="relative">
                            {/* Time Slot Button */}
                            <button
                              onClick={() => !isBlocked && handleInputChange('appointment_time', slot.value)}
                              disabled={isBlocked && !isStaffMember}
                              className={`w-full p-2.5 text-center rounded-lg border-2 transition-all ${
                                isBlocked && !isStaffMember
                                  ? 'border-red-200 bg-red-50 text-red-400 cursor-not-allowed'
                                  : isBlocked && isStaffMember
                                    ? 'border-red-300 bg-red-100 text-red-700'
                                    : formData.appointment_time === slot.value
                                      ? ''
                                      : 'border-gray-200 bg-white hover:border-gray-300 text-gray-900'
                              }`}
                              style={
                                isBlocked && !isStaffMember
                                  ? {}
                                  : isBlocked && isStaffMember
                                    ? { borderColor: '#fca5a5', backgroundColor: '#fef2f2' }
                                    : formData.appointment_time === slot.value 
                                      ? {
                                          borderColor: primaryColor,
                                          backgroundColor: 'var(--theme-primary-light)',
                                          color: primaryColor
                                        } 
                                      : {}
                              }
                            >
                              <Clock className="h-4 w-4 mx-auto mb-1" />
                              <div className="text-sm font-medium">
                                {slot.display || formatTime(slot.time)}
                              </div>
                              {isBlocked && (
                                <div className="text-xs mt-1 flex items-center justify-center">
                                  <Lock className="h-3 w-3 mr-1" />
                                  {isStaffMember ? (
                                    <span className="text-red-600">
                                      Blocked{blockedInfo?.reason ? `: ${blockedInfo.reason.substring(0, 20)}${blockedInfo.reason.length > 20 ? '...' : ''}` : ''}
                                    </span>
                                  ) : (
                                    <span className="text-red-500">Blocked</span>
                                  )}
                                </div>
                              )}
                            </button>
                            
                            {/* Staff Controls - Only visible to managers and technicians */}
                            {isStaffMember && (
                              <div className="absolute top-2 right-2 flex space-x-1">
                                {isBlocked ? (
                                  <button
                                    onClick={() => handleUnblockSlot(blockedInfo.id, slot.display || formatTime(slot.time))}
                                    className="p-1 bg-green-500 hover:bg-green-600 text-white rounded-full transition-colors"
                                    title={`Unblock ${slot.display || formatTime(slot.time)}${blockedInfo?.reason ? ` - ${blockedInfo.reason}` : ''}`}
                                  >
                                    <Unlock className="h-3 w-3" />
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => openBlockModal(slot.time, slot.display || formatTime(slot.time))}
                                    className="p-1 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors"
                                    title={`Block ${slot.display || formatTime(slot.time)}`}
                                  >
                                    <Lock className="h-3 w-3" />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Tab 1: Owner Information */}
            {currentTab === 1 && (
              <div>
                <div className="mb-4">
                  <h2 className="text-base font-semibold text-gray-900">Pet Owner Information</h2>
                </div>
                
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        value={formData.owner_first_name}
                        onChange={(e) => handleInputChange('owner_first_name', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': primaryColor }}
                        onFocus={(e) => {
                          e.target.style.borderColor = primaryColor;
                          e.target.style.boxShadow = `0 0 0 2px rgba(41, 173, 211, 0.2)`;
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#d1d5db';
                          e.target.style.boxShadow = 'none';
                        }}
                        placeholder="Enter your first name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        value={formData.owner_last_name}
                        onChange={(e) => handleInputChange('owner_last_name', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': primaryColor }}
                        onFocus={(e) => {
                          e.target.style.borderColor = primaryColor;
                          e.target.style.boxShadow = `0 0 0 2px rgba(41, 173, 211, 0.2)`;
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#d1d5db';
                          e.target.style.boxShadow = 'none';
                        }}
                        placeholder="Enter your last name"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address {isStaffMember ? "(Optional)" : "*"}
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': primaryColor }}
                        onFocus={(e) => {
                          e.target.style.borderColor = primaryColor;
                          e.target.style.boxShadow = `0 0 0 2px rgba(41, 173, 211, 0.2)`;
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#d1d5db';
                          e.target.style.boxShadow = 'none';
                        }}
                        placeholder="Enter email address"
                        required={!isStaffMember}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': primaryColor }}
                        onFocus={(e) => {
                          e.target.style.borderColor = primaryColor;
                          e.target.style.boxShadow = `0 0 0 2px rgba(41, 173, 211, 0.2)`;
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#d1d5db';
                          e.target.style.boxShadow = 'none';
                        }}
                        placeholder="Enter your phone number"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tab 2: Pet Information */}
            {currentTab === 2 && (
              <div>
                <div className="mb-4">
                  <h2 className="text-base font-semibold text-gray-900">Pet Information</h2>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pet Name {isStaffMember ? "(Optional)" : "*"}
                    </label>
                    <input
                      type="text"
                      value={formData.pet_name}
                      onChange={(e) => handleInputChange('pet_name', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                      style={{ '--tw-ring-color': primaryColor }}
                      onFocus={(e) => {
                        e.target.style.borderColor = primaryColor;
                        e.target.style.boxShadow = `0 0 0 2px rgba(41, 173, 211, 0.2)`;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#d1d5db';
                        e.target.style.boxShadow = 'none';
                      }}
                      placeholder="Enter pet's name"
                      required={!isStaffMember}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-4">
                      Pet Type {isStaffMember ? "(Optional)" : "*"}
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => handleInputChange('pet_type', 'dog')}
                        className={`p-6 rounded-lg border-2 transition-all text-center ${
                          formData.pet_type === 'dog'
                            ? 'border-theme-blue bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="text-4xl mb-2">🐕</div>
                        <div className="font-semibold">Dog</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleInputChange('pet_type', 'cat')}
                        className={`p-6 rounded-lg border-2 transition-all text-center ${
                          formData.pet_type === 'cat'
                            ? 'border-theme-blue bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="text-4xl mb-2">🐱</div>
                        <div className="font-semibold">Cat</div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tab 3: Reason for Visit */}
            {currentTab === 3 && (
              <div>
                <div className="mb-4">
                  <h2 className="text-base font-semibold text-gray-900">Reason for Visit</h2>
                </div>
                
                <div>
                  <select
                    value={formData.reason_for_visit}
                    onChange={(e) => handleInputChange('reason_for_visit', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                    style={{ '--tw-ring-color': primaryColor }}
                    onFocus={(e) => {
                      e.target.style.borderColor = primaryColor;
                      e.target.style.boxShadow = `0 0 0 2px rgba(41, 173, 211, 0.2)`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                    required={!isStaffMember}
                  >
                    <option value="">Select reason for visit</option>
                    {reasonOptions.map((reason, index) => (
                      <option key={index} value={reason}>
                        {reason}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Tab 4: Additional Information */}
            {currentTab === 4 && (
              <div>
                <div className="mb-4">
                  <h2 className="text-base font-semibold text-gray-900">Other Details</h2>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Primary Veterinary Hospital Name (Optional)
                    </label>
                    <input
                      type="text"
                      value={formData.primary_vet_hospital}
                      onChange={(e) => handleInputChange('primary_vet_hospital', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                      style={{ '--tw-ring-color': primaryColor }}
                      onFocus={(e) => {
                        e.target.style.borderColor = primaryColor;
                        e.target.style.boxShadow = `0 0 0 2px rgba(41, 173, 211, 0.2)`;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#d1d5db';
                        e.target.style.boxShadow = 'none';
                      }}
                      placeholder="Enter your regular vet's name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      How did you hear about us? (Optional)
                    </label>
                    <select
                      value={formData.how_heard_about_us}
                      onChange={(e) => handleInputChange('how_heard_about_us', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                      style={{ '--tw-ring-color': primaryColor }}
                      onFocus={(e) => {
                        e.target.style.borderColor = primaryColor;
                        e.target.style.boxShadow = `0 0 0 2px rgba(41, 173, 211, 0.2)`;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#d1d5db';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <option value="">Please select</option>
                      {howHeardOptions.map((option, index) => (
                        <option key={index} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Tab 5: Review & Confirm */}
            {currentTab === 5 && (
              <div>
                <div className="mb-4">
                  <h2 className="text-base font-semibold text-gray-900">Review & Confirm Your Appointment</h2>
                </div>

                <div className="space-y-3">
                  {/* Appointment Time */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-semibold text-blue-900 mb-1">Appointment Time</h3>
                        <p className="text-sm text-blue-800">
                          <strong>Today</strong> at <strong>{formatAppointmentTime(formData.appointment_time)}</strong>
                        </p>
                      </div>
                      <button
                        onClick={() => goToTab(0)}
                        className="text-theme-blue hover:text-blue-800 text-xs font-medium"
                      >
                        Change
                      </button>
                    </div>
                  </div>

                  {/* Pet Owner Information */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-gray-900">Pet Owner Information</h3>
                      <button
                        onClick={() => goToTab(1)}
                        className="text-theme-blue hover:text-blue-800 text-xs font-medium"
                      >
                        Edit
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-0.5">Name</label>
                        <p className="text-sm text-gray-900">{formData.owner_first_name} {formData.owner_last_name}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-0.5">Phone</label>
                        <p className="text-sm text-gray-900">{formData.phone}</p>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-500 mb-0.5">Email</label>
                        <p className="text-sm text-gray-900">{formData.email}</p>
                      </div>
                    </div>
                  </div>

                  {/* Pet Information */}
                  <div className="bg-white border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-gray-900">Pet Information</h3>
                      <button
                        onClick={() => goToTab(2)}
                        className="text-theme-blue hover:text-blue-800 text-xs font-medium"
                      >
                        Edit
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-0.5">Pet Name</label>
                        <p className="text-sm text-gray-900">{formData.pet_name}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-0.5">Pet Type</label>
                        <p className="text-sm text-gray-900 capitalize">{formData.pet_type}</p>
                      </div>
                    </div>
                  </div>

                  {/* Visit Reason */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-gray-900">Reason for Visit</h3>
                      <button
                        onClick={() => goToTab(3)}
                        className="text-theme-blue hover:text-blue-800 text-xs font-medium"
                      >
                        Edit
                      </button>
                    </div>
                    <p className="text-sm text-gray-900">{formData.reason_for_visit}</p>
                  </div>

                  {/* Additional Information */}
                  <div className="bg-white border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-gray-900">Other Details</h3>
                      <button
                        onClick={() => goToTab(4)}
                        className="text-theme-blue hover:text-blue-800 text-xs font-medium"
                      >
                        Edit
                      </button>
                    </div>
                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-0.5">Primary Veterinary Hospital</label>
                        <p className="text-sm text-gray-900">{formData.primary_vet_hospital}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-0.5">How did you hear about us?</label>
                        <p className="text-sm text-gray-900">{formData.how_heard_about_us}</p>
                      </div>
                    </div>
                  </div>

                  {/* Important Notice */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="flex items-start">
                      <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5 mr-2" />
                      <div>
                        <h3 className="text-xs font-semibold text-yellow-800 mb-1">Important Notice</h3>
                        <ul className="text-xs text-yellow-700 space-y-0.5">
                          <li>• Please arrive 10 minutes before your scheduled appointment</li>
                          <li>• Bring any previous medical records if available</li>
                          <li>• Have your pet secured on a leash or in a carrier</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tab 6: Success */}
            {currentTab === 6 && (
              <div className="text-center py-4">
                {/* Personalized Message Section */}
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <p className="text-sm text-gray-600 leading-relaxed">
                    <span className="font-semibold text-gray-900">
                      {capitalizeWords(formData.owner_first_name)} {capitalizeWords(formData.owner_last_name)}
                    </span>, your urgent care appointment has been successfully booked. 
                    We look forward to caring for <span className="font-semibold text-gray-900">{capitalizeWords(formData.pet_name)}</span>.
                  </p>
                </div>

                {/* Appointment Summary Card */}
                <div className="max-w-3xl mx-auto mb-4">
                  <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                    <div className="text-center mb-2">
                      <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: primaryColor }}>
                        <Clock className="h-3 w-3 mr-1" />
                        Appointment Summary
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                      <div className="flex items-center justify-between md:justify-start">
                        <span className="font-medium text-gray-600 mr-3">Client:</span>
                        <span className="font-semibold text-gray-900">
                          {capitalizeWords(formData.owner_first_name)} {capitalizeWords(formData.owner_last_name)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between md:justify-start">
                        <span className="font-medium text-gray-600 mr-3">Date:</span>
                        <span className="font-semibold text-gray-900">{formatDateAndDay()}</span>
                      </div>
                      <div className="flex items-center justify-between md:justify-start">
                        <span className="font-medium text-gray-600 mr-3">Time:</span>
                        <span className="font-semibold text-gray-900">{formatAppointmentTime(formData.appointment_time)}</span>
                      </div>
                      <div className="flex items-center justify-between md:justify-start">
                        <span className="font-medium text-gray-600 mr-3">Pet:</span>
                        <span className="font-semibold text-gray-900">
                          {capitalizeWords(formData.pet_name)} ({capitalizeWords(formData.pet_type)})
                        </span>
                      </div>
                      <div className="flex items-center justify-between md:justify-start">
                        <span className="font-medium text-gray-600 mr-3">Phone:</span>
                        <span className="font-semibold text-gray-900">{formatPhoneNumber(formData.phone)}</span>
                      </div>
                      <div className="flex items-center justify-between md:justify-start">
                        <span className="font-medium text-gray-600 mr-3">Email:</span>
                        <span className="font-semibold text-gray-900">{formData.email}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Important Reminders */}
                <div className="max-w-3xl mx-auto mb-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="flex items-start">
                      <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
                      <div className="text-left">
                        <h3 className="text-xs font-semibold text-yellow-800 mb-1">Important Reminders</h3>
                        <ul className="text-xs text-yellow-700 space-y-0.5">
                          <li>• Please arrive 10 minutes before your scheduled time</li>
                          <li>• Bring any previous medical records if available</li>
                          <li>• Have your pet secured on a leash or in a carrier</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Button */}
                <div className="mb-8">
                  <Link 
                    to="/urgent-care" 
                    className="inline-flex items-center px-4 py-2 text-sm text-white font-semibold rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <Stethoscope className="h-4 w-4 mr-2" />
                    Urgent Care Services
                  </Link>
                </div>

                {/* Contact CTA Section - Styled like Dog Dental Care */}
                <section style={{ 
                  background: `linear-gradient(135deg, ${primaryColor} 0%, var(--theme-primary-hover) 100%)`, 
                  paddingTop: '30px', 
                  paddingBottom: '30px',
                  borderRadius: '12px',
                  marginTop: '2rem'
                }}>
                  <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="font-bold text-white mb-6" style={{ fontSize: '1rem' }}>
                      Need to Make Changes to Your Appointment?
                    </h2>
                    <p className="mb-8" style={{ color: 'white', fontSize: '1rem' }}>
                      Please call us if you need to reschedule or have any questions about your upcoming visit
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <a
                        href={`tel:${currentBusinessInfo.phone}`}
                        className="inline-flex items-center justify-center bg-white px-5 py-2 text-sm rounded-lg font-semibold transition-colors duration-200"
                        style={{ color: primaryColor }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                      >
                        <Phone className="h-4 w-4 mr-2" />
                        Call {currentBusinessInfo.phone}
                      </a>
                    </div>
                  </div>
                </section>
              </div>
            )}

            {/* Navigation Buttons */}
            {currentTab < 6 && availableToday && timeSlots.length > 0 && (
              <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                {currentTab > 0 && (
                  <button
                    onClick={prevStep}
                    className="flex items-center px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Previous
                  </button>
                )}
                
                <div className="ml-auto">
                  {currentTab < 4 ? (
                    <button
                      onClick={nextStep}
                      disabled={!validateStep()}
                      className="flex items-center px-5 py-2 text-sm text-white rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ backgroundColor: validateStep() ? primaryColor : '#9ca3af' }}
                    >
                      Next
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </button>
                  ) : currentTab === 4 ? (
                    <button
                      onClick={nextStep}
                      disabled={!validateStep()}
                      className="flex items-center px-5 py-2 text-sm text-white rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ backgroundColor: validateStep() ? primaryColor : '#9ca3af' }}
                    >
                      Review Appointment
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </button>
                  ) : currentTab === 5 ? (
                    <button
                      onClick={submitAppointment}
                      disabled={loading}
                      className="flex items-center px-5 py-2 text-sm text-white rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ backgroundColor: !loading ? primaryColor : '#9ca3af' }}
                    >
                      {loading ? 'Booking...' : 'Confirm & Book Appointment'}
                      {!loading && <CheckCircle className="h-4 w-4 ml-2" />}
                    </button>
                  ) : null}
                </div>
              </div>
            )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Block Slot Modal - Only visible to staff members */}
      {showBlockModal && isStaffMember && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md bg-white rounded-lg shadow-lg">
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Block Time Slot</h3>
              <button
                onClick={closeBlockModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-center">
                  <Lock className="h-5 w-5 text-yellow-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">
                      Block {selectedSlotForBlock?.display} slot
                    </p>
                    <p className="text-xs text-yellow-700">
                      This slot will not be available for online booking
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for blocking (optional)
                </label>
                <textarea
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  placeholder="e.g., Emergency surgery, Staff meeting, Equipment maintenance..."
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                  style={{ focusRingColor: primaryColor }}
                />
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={closeBlockModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleBlockSlot}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-red-400 rounded-md"
              >
                {loading ? 'Blocking...' : 'Block Slot'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unblock Slot Confirmation Modal - Only visible to staff members */}
      {showUnblockModal && isStaffMember && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md bg-white rounded-lg shadow-lg">
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Unblock Time Slot</h3>
              <button
                onClick={closeUnblockModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center">
                  <Unlock className="h-5 w-5 text-green-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-green-800">
                      Unblock {selectedSlotForUnblock?.time} slot
                    </p>
                    <p className="text-xs text-green-700">
                      This slot will become available for online booking
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-sm text-gray-700">
                  Are you sure you want to unblock this time slot? This action cannot be undone.
                </p>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={closeUnblockModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={confirmUnblockSlot}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-md"
              >
                {loading ? 'Unblocking...' : 'Unblock Slot'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UrgentCareBooking;